import { css } from "@emotion/css";
import { Checkbox, Dropdown, Option, Input } from "@fluentui/react-components";
import React, { useContext } from "react";
import { Flex } from "../components-utils";
import { useNumberFormat } from "../utils/use-number-format/useNumberFormat";
import {
  Column,
  NumberFilter as NumberFilterType,
  NumberFilterOperator,
  numberFilterOperators,
} from "./types";
import styled from "@emotion/styled";
import { ColumnOptionsContext } from "./ColumnOptions";
import { capitalize } from "../utils/text";

type Props = {
  column: Column;
};

export const NumberFilter = ({ column }: Props) => {
  const filter = column.filter as NumberFilterType;
  const { setFilter, strings, id } = useContext(ColumnOptionsContext);

  const showCheckboxes =
    filter.operator !== "eq" || filter.operator === ("between" as any);

  return (
    <div
      className={css`
        height: 180px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      `}
    >
      <Dropdown
        value={
          strings[`numberFilterOperator${capitalize(filter.operator)}Label`]
        }
        selectedOptions={[filter.operator]}
        id={id ? `${id}-numberPicker-${column.key}` : undefined}
        onOptionSelect={(_, data) => {
          if (!data.optionValue) return;
          setFilter(column.key, {
            ...filter,
            operator: data.optionValue as NumberFilterOperator,
          });
        }}
      >
        {numberFilterOperators.map((o) => (
          <Option key={o} value={o}>
            {strings[`numberFilterOperator${capitalize(o)}Label`]}
          </Option>
        ))}
      </Dropdown>
      {showCheckboxes && (
        <Flex>
          {filter.operator !== "eq" && (
            <Checkbox
              label={strings.numberFilterIncludeLabel}
              checked={filter.includeNumber1}
              onChange={(_, data) => {
                setFilter(column.key, {
                  ...filter,
                  includeNumber1: data.checked === true,
                });
              }}
            />
          )}
          {filter.operator === "between" && (
            <ReverseCheckbox
              label={strings.numberFilterIncludeLabel}
              checked={filter.includeNumber2}
              id={id ? `${id}-reverseCheckBox-${column.key}` : undefined}
              onChange={(_, data) => {
                setFilter(column.key, {
                  ...filter,
                  includeNumber2: data.checked === true,
                });
              }}
            />
          )}
        </Flex>
      )}
      <div
        className={css`
          display: grid;
          grid-template-columns: 50% 50%;
          grid-gap: 5px;
        `}
      >
        <NumberField
          value={filter.number1 !== null ? filter.number1.toString() : ""}
          onChange={(value) => {
            setFilter(column.key, {
              ...filter,
              number1: (value === "" ? null : value) as any,
            });
          }}
          id={id ? `${id}-numberFilter1-${column.key}` : undefined}
          {...filter}
        />
        {filter.operator === "between" && (
          <NumberField
            value={filter.number2 !== null ? filter.number2.toString() : ""}
            onChange={(value) => {
              setFilter(column.key, {
                ...filter,
                number2: (value === "" ? null : value) as any,
              });
            }}
            id={id ? `${id}-numberFilter2-${column.key}` : undefined}
            {...filter}
          />
        )}
      </div>
      <FormulaText>
        {filter.operator === "gt" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          `${filter.includeNumber1 ? ">=" : ">"} ${filter.number1}`}
        {filter.operator === "lt" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          `${filter.includeNumber1 ? "<=" : "<"} ${filter.number1}`}
        {filter.operator === "eq" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          `= ${filter.number1}`}
        {filter.operator === "between" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          filter.number2 !== null &&
          filter.number2 !== undefined &&
          `${filter.includeNumber1 ? ">=" : ">"} ${filter.number1} ${
            strings.numberFilterAndLabel
          } ${filter.includeNumber2 ? "<=" : "<"} ${filter.number2}`}
      </FormulaText>
    </div>
  );
};

const FormulaText = styled.div`
  color: ${(p) => p.theme.palette.neutralPrimary};
  font-style: italic;
  margin-top: auto;
  font-weight: bold;
`;

const ReverseCheckbox = styled(Checkbox)`
  margin-left: auto;
  .ms-Checkbox-label {
    flex-direction: row-reverse;
  }
  .ms-Checkbox-text {
    margin: 0;
    margin-right: 4px;
  }
  .ms-Checkbox-checkbox {
    margin: 0;
    margin-left: 4px;
  }
`;

const NumberField = ({
  value,
  onChange,
  max,
  placeholder,
  isNegativeAllowed = false,
  includeThousandSeparators,
  format,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  max?: number;
  placeholder?: string;
  isNegativeAllowed?: boolean;
  includeThousandSeparators?: boolean;
  format?: "integer" | "float";
  id?: string;
}) => {
  const { value: _value, onChange: _onChange } = useNumberFormat({
    value,
    setValue: (value: string) => onChange(value),
    isNegativeAllowed,
    max,
    includeThousandSeparators,
    format,
  });
  return (
    <Input
      id={id}
      value={_value}
      onChange={(_, data) => {
        _onChange({ target: { value: data.value } } as any);
      }}
      placeholder={placeholder}
    />
  );
};
